import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import UserAccountMenu from '../../components/ui/UserAccountMenu';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import SupportTypeCard from './components/SupportTypeCard';
import IntakeForm from './components/IntakeForm';
import SchedulingCalendar from './components/SchedulingCalendar';
import ConfirmationModal from './components/ConfirmationModal';
import { supabase } from '../../lib/supabase';

const ScheduleMarketingSupport = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedSupportType, setSelectedSupportType] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState(null);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    role: '',
    practiceName: '',
    city: '',
    state: '',
    marketingGoal: '',
    currentEffort: '',
    challenges: '',
    platforms: {
      linkedin: false,
      facebook: false,
      instagram: false,
      twitter: false
    },
    website: '',
    additionalNotes: '',
    agreedToContact: false
  });

  const [errors, setErrors] = useState({});

  const validateStep1 = () => {
    if (!selectedSupportType) {
      return { supportType: 'Please select a consultation type' };
    }
    return {};
  };

  const validateStep2 = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData?.role) {
      newErrors.role = 'Please select your role';
    }

    if (!formData?.practiceName?.trim()) {
      newErrors.practiceName = 'Practice name is required';
    }

    if (!formData?.city?.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData?.state?.trim()) {
      newErrors.state = 'State is required';
    }

    if (!formData?.marketingGoal) {
      newErrors.marketingGoal = 'Please select a marketing goal';
    }

    if (!formData?.currentEffort) {
      newErrors.currentEffort = 'Please select your current effort level';
    }

    if (!formData?.challenges?.trim()) {
      newErrors.challenges = 'Please describe your challenges or questions';
    } else if (formData?.challenges?.trim()?.length < 20) {
      newErrors.challenges = 'Please provide more detail (minimum 20 characters)';
    }

    if (!formData?.agreedToContact) {
      newErrors.agreedToContact = 'You must agree to receive communications';
    }

    return newErrors;
  };

  const validateStep3 = () => {
    if (!selectedSlot) {
      return { slot: 'Please select an appointment time' };
    }
    return {};
  };

  const handleNext = () => {
    let stepErrors = {};

    if (currentStep === 1) {
      stepErrors = validateStep1();
    } else if (currentStep === 2) {
      stepErrors = validateStep2();
    } else if (currentStep === 3) {
      stepErrors = validateStep3();
    }

    if (Object.keys(stepErrors)?.length > 0) {
      setErrors(stepErrors);
      return;
    }

    setErrors({});
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    setErrors({});
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    let stepErrors = validateStep3();

    if (Object.keys(stepErrors)?.length > 0) {
      setErrors(stepErrors);
      return;
    }

    setIsSubmitting(true);
    setEmailError(null);

    try {
      // Store booking data in Supabase database
      const bookingData = {
        support_type: selectedSupportType,
        appointment_date: selectedSlot?.date,
        appointment_time: selectedSlot?.displayTime,
        full_name: formData?.fullName,
        email: formData?.email,
        phone: formData?.phone,
        role: formData?.role,
        practice_name: formData?.practiceName,
        city: formData?.city,
        state: formData?.state,
        marketing_goal: formData?.marketingGoal,
        current_effort: formData?.currentEffort,
        challenges: formData?.challenges,
        platforms: formData?.platforms,
        website: formData?.website || '',
        additional_notes: formData?.additionalNotes || ''
      };

      // Insert booking into database
      const { data: insertData, error: insertError } = await supabase?.from('marketing_support_bookings')?.insert([bookingData])?.select();

      if (insertError) {
        console.error('Database error:', insertError);
        throw new Error('Failed to save booking. Please try again.');
      }

      console.log('Booking saved successfully:', insertData);

      // Send notification email via edge function
      try {
        const { data: functionData, error: functionError } = await supabase?.functions?.invoke(
          'send-booking-email',
          {
            body: {
              bookingData: {
                supportType: selectedSupportType,
                slot: selectedSlot,
                formData: formData
              }
            }
          }
        );

        if (functionError) {
          console.error('Email notification error:', functionError);
          // Don't throw - booking is saved, email is secondary
        } else {
          console.log('Email notification sent successfully:', functionData);
        }
      } catch (emailErr) {
        console.error('Email notification failed:', emailErr);
        // Don't throw - booking is saved, email is secondary
      }

      setIsSubmitting(false);
      setShowConfirmation(true);
    } catch (error) {
      console.error('Booking submission error:', error);
      setEmailError(error?.message || 'An error occurred while saving your booking. Please contact support.');
      setIsSubmitting(false);
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    navigate('/providers-dashboard');
  };

  const steps = [
    { number: 1, label: 'Support Type', icon: 'Target' },
    { number: 2, label: 'Your Information', icon: 'User' },
    { number: 3, label: 'Schedule Time', icon: 'Calendar' }
  ];

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      <div className="flex-1 lg:ml-60">
        <header className="sticky top-0 z-100 bg-white border-b border-border h-20 flex items-center justify-between px-4 md:px-6 lg:px-8">
          <div>
            <h1 className="text-xl md:text-2xl font-light uppercase text-foreground">
              Schedule Marketing Support
            </h1>
            <p className="text-sm text-muted-foreground font-caption mt-1">
              Book a personalized consultation with our marketing experts
            </p>
          </div>

          <UserAccountMenu />
        </header>

        <main className="p-4 md:p-6 lg:p-8">
          <div className="max-w-5xl mx-auto">
            <div className="mb-6 md:mb-8">
              <div className="flex items-center justify-between">
                {steps?.map((step, index) => (
                  <React.Fragment key={step?.number}>
                    <div className="flex flex-col items-center flex-1">
                      <div
                        className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-250 ${
                          currentStep >= step?.number
                            ? 'bg-primary text-primary-foreground shadow-md'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {currentStep > step?.number ? (
                          <Icon name="Check" size={20} />
                        ) : (
                          <Icon name={step?.icon} size={20} />
                        )}
                      </div>
                      <span
                        className={`text-xs md:text-sm font-medium mt-2 text-center ${
                          currentStep >= step?.number ? 'text-foreground' : 'text-muted-foreground'
                        }`}
                      >
                        {step?.label}
                      </span>
                    </div>

                    {index < steps?.length - 1 && (
                      <div className="flex-1 h-0.5 mx-2 md:mx-4 mb-8">
                        <div
                          className={`h-full transition-all duration-250 ${
                            currentStep > step?.number ? 'bg-primary' : 'bg-border'
                          }`}
                        />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg shadow-sm p-6 md:p-8">
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                      Choose Your Consultation Type
                    </h2>
                    <p className="text-sm md:text-base text-muted-foreground">
                      Select the type of marketing support that best fits your needs
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <SupportTypeCard
                      type="strategy"
                      isSelected={selectedSupportType === 'strategy'}
                      onSelect={setSelectedSupportType}
                    />
                    <SupportTypeCard
                      type="content"
                      isSelected={selectedSupportType === 'content'}
                      onSelect={setSelectedSupportType}
                    />
                    <SupportTypeCard
                      type="technical"
                      isSelected={selectedSupportType === 'technical'}
                      onSelect={setSelectedSupportType}
                    />
                    <SupportTypeCard
                      type="training"
                      isSelected={selectedSupportType === 'training'}
                      onSelect={setSelectedSupportType}
                    />
                  </div>

                  {errors?.supportType && (
                    <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                      <p className="text-sm text-destructive flex items-center gap-2">
                        <Icon name="AlertCircle" size={16} />
                        {errors?.supportType}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {currentStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                      Tell Us About Yourself
                    </h2>
                    <p className="text-sm md:text-base text-muted-foreground">
                      Help us prepare for your consultation by providing some background information
                    </p>
                  </div>

                  <IntakeForm formData={formData} onChange={setFormData} errors={errors} />
                </div>
              )}

              {currentStep === 3 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                      Select Your Appointment Time
                    </h2>
                    <p className="text-sm md:text-base text-muted-foreground">
                      Choose a convenient time slot for your consultation
                    </p>
                  </div>

                  <SchedulingCalendar
                    selectedType={selectedSupportType}
                    onSelectSlot={setSelectedSlot}
                    selectedSlot={selectedSlot}
                  />

                  {errors?.slot && (
                    <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                      <p className="text-sm text-destructive flex items-center gap-2">
                        <Icon name="AlertCircle" size={16} />
                        {errors?.slot}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t border-border">
                {currentStep > 1 && (
                  <Button
                    variant="outline"
                    onClick={handleBack}
                    iconName="ChevronLeft"
                    iconPosition="left"
                    className="sm:w-auto"
                  >
                    Back
                  </Button>
                )}

                <div className="flex-1" />

                {currentStep < 3 ? (
                  <Button
                    variant="default"
                    onClick={handleNext}
                    iconName="ChevronRight"
                    iconPosition="right"
                    className="sm:w-auto"
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    variant="default"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                    iconName="CheckCircle"
                    iconPosition="left"
                    className="sm:w-auto"
                  >
                    {isSubmitting ? 'Confirming...' : 'Confirm Appointment'}
                  </Button>
                )}
              </div>
            </div>

            <div className="mt-6 bg-muted/50 rounded-lg p-4 md:p-6">
              <div className="flex items-start gap-3">
                <Icon name="HelpCircle" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-base font-semibold text-foreground mb-2">Need Help?</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    If you have questions about scheduling or need immediate assistance, please contact
                    our support team.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <a
                      href="mailto:support@viderasurgical.com"
                      className="text-sm transition-colors duration-250 flex items-center gap-2"
                      style={{ color: 'var(--corvid-background-color, rgba(var(--bg, var(--color_17, 37, 99, 235)), var(--alpha-bg, 1)))' }}
                    >
                      <Icon name="Mail" size={16} />
                      support@viderasurgical.com
                    </a>
                    <span className="hidden sm:inline text-muted-foreground">•</span>
                    <a
                      href="tel:+15551234567"
                      className="text-sm transition-colors duration-250 flex items-center gap-2"
                      style={{ color: 'var(--corvid-background-color, rgba(var(--bg, var(--color_17, 37, 99, 235)), var(--alpha-bg, 1)))' }}
                    >
                      <Icon name="Phone" size={16} />
                      (555) 123-4567
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={handleCloseConfirmation}
        appointmentDetails={{
          supportType: selectedSupportType,
          slot: selectedSlot,
          formData: formData
        }}
        emailError={emailError}
      />
    </div>
  );
};

export default ScheduleMarketingSupport;